import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

export const useQuoteStore = defineStore('quote', () => {
  const suiteStore = useSuiteStore()
  const quote = ref(null)
  const isLoadingQuote = ref(false)

  const contractTypes = ref<{ code: string; label: string }[]>([])

  const fetchQuote = async (search: string) => {
    isLoadingQuote.value = true
    try {
      const response = await suiteStore.suiteFetch(`quote/${search}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        quote.value = data
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du devis:', error)
    } finally {
      isLoadingQuote.value = false
    }
    return null
  }

  const fetchContractTypes = async () => {
    try {
      const response = await suiteStore.suiteFetch('quote/contract-types/list', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        contractTypes.value = data?.data ?? data ?? []
        return contractTypes.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des types de contrat:', error)
    }
    return []
  }

  const syncQuote = async (
    quoteRef: string,
    source?: string,
  ): Promise<{ ok: boolean; quote?: unknown; errorMessage?: string }> => {
    try {
      const qs = source ? `?source=${encodeURIComponent(source)}` : ''
      const response = await suiteStore.suiteFetch(`quote/${encodeURIComponent(quoteRef)}/sync${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        try {
          const body = await response.json()
          const data = body?.data ?? body
          return { ok: true, quote: data }
        } catch {
          return { ok: true }
        }
      }
      let errorMessage: string | undefined
      if (response && typeof response !== 'boolean') {
        try {
          const err = await response.json()
          const raw = err?.message ?? err?.error
          errorMessage = typeof raw === 'string' ? raw : raw != null ? String(raw) : undefined
        } catch {
          // ignore
        }
      }
      return { ok: false, errorMessage }
    } catch (error) {
      console.error('Erreur lors de la synchronisation du devis:', error)
      return { ok: false, errorMessage: error instanceof Error ? error.message : undefined }
    }
  }

  const signManually = async (quoteUuid: string, signedManuallyAt: string, orderUuid?: string): Promise<boolean> => {
    try {
      const response = await suiteStore.suiteFetch(`quote/${quoteUuid}/sign-manually`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ signedManuallyAt, ...(orderUuid ? { orderUuid } : {}) }),
      })
      return !!(response && typeof response !== 'boolean' && response.ok)
    } catch (error) {
      console.error('Erreur lors de la signature manuelle du devis:', error)
      return false
    }
  }

  return {
    quote,
    contractTypes,
    fetchQuote,
    fetchContractTypes,
    syncQuote,
    signManually,
  }
})
